import {Component, Input} from "@angular/core";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {GameTypeEnum} from "../../../enum/GameTypeEnum";
import {ReportingService} from "../../../services/reporting.service";
import * as pdfMake from 'pdfmake/build/pdfmake';
import {Content, TDocumentDefinitions} from "pdfmake/interfaces";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {DatePipe} from "@angular/common";
import {PdfUtilityService} from "../../../services/pdf-utility.service";
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {ReportBaseComponent} from "../../../pages/reports/report-base.component";
import {IGameInstanceShared} from "../../../interfaces/IGameInstanceShared";
import {ICharityContributionsBreakdown} from "../../../interfaces/ICharityContributionsBreakdown";
import {
  ComponentLevelErrorMessageComponent
} from "../../display-errors/component-level-error-message/component-level-error-message.component";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {MatButton, MatIconButton} from "@angular/material/button";
import {PipesModule} from "../../../pipes/pipes.module";
import {AgGridAngular} from "ag-grid-angular";
import {WinnerPayoutStatusPipe} from "../../../pipes/winner-payout-status.pipe";
import {
  ShouldHidePlayersNamePdfComponent
} from "../../dialogs/should-hide-players-name-pdf/should-hide-players-name-pdf.component";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {IGameInstanceSummaryReport} from "../../../interfaces/IGameInstanceSummaryReport";
import {WinnerPayoutStatusTypes} from "../../../enum/WinnerPayoutStatusTypes";
import {UtilService} from "../../../services/util.service";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatIconModule} from "@angular/material/icon";
import {MatTooltipModule} from "@angular/material/tooltip";
import {FieldHelpIconComponent} from "../../../shared/field-help-icon/field-help-icon.component";

(<any>pdfMake).vfs = pdfFonts.pdfMake.vfs;

@Component({
  selector: 'app-standard-game-report',
  standalone: true,
  templateUrl: './standard-game-report.component.html',
  imports: [
    ComponentLevelErrorMessageComponent,
    LoadingCardContentComponent,
    DatePipe,
    MatButton,
    MatIconButton,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PipesModule,
    AgGridAngular,
    FieldHelpIconComponent
  ],
  styleUrls: ['../../../pages/reports/reports.scss', './standard-game-report.component.scss']
})
export class StandardGameReportComponent extends ReportBaseComponent {
  @Input() set gameSetter(gameP: IGameQueryResult) {
    this.activeGame.set(gameP);
  }

  public standardGameReport: IGameInstanceSummaryReport | null = null;
  public totalFees: number = 0;
  public totalDisputeFees: number = 0;

  constructor(reportingService: ReportingService,
              currencyPipe: FormatCurrencyPipe,
              winnerPayoutStatusPipe: WinnerPayoutStatusPipe,
              pdfUtilityService: PdfUtilityService,
              datePipe: DatePipe,
              private utilService: UtilService) {
    super(reportingService, currencyPipe, winnerPayoutStatusPipe, pdfUtilityService, datePipe);
  }

  generateReport(instanceP: IGameInstanceShared) {
    this.uiState = UIStateEnum.ShowLoading;
    this.standardGameReport = null;

    this.reportingService.getStandardGameInstanceReportData(instanceP).subscribe({
      next: (reportP) => {
        this.standardGameReport = reportP;
        this.calculateTotalFees(this.standardGameReport);
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.errorMessage = 'Looks there is no draw report data available for this game';
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  calculateTotalFees(standardGameReportP: IGameInstanceSummaryReport) {
    this.totalFees = standardGameReportP.HstOnCausableFees +
      standardGameReportP.TotalCausableFees +
      standardGameReportP.TotalStripeFees +
      standardGameReportP.TotalAdministrationCommissionFees +
      standardGameReportP.TotalRefundsCharityContribution +
      standardGameReportP.TotalStripeDisputeFees +
      standardGameReportP.TotalCharityDisputeFees +
      standardGameReportP.TotalDisputeCharityContribution;

    this.totalDisputeFees = standardGameReportP.TotalStripeDisputeFees + standardGameReportP.TotalCharityDisputeFees;
  }

  onGeneratePdfClick() {
    // if the game is set to not display the non winners name, and the draw was a rollover
    // prompt the admin to see if they do or do not want to see the winners name on the report
    let gameHidesNonWinnerAndDrawWasRollover = !this.activeGame()?.DisplayNonWinnerName && !this.standardGameReport?.WinnerPlayed;

    if (gameHidesNonWinnerAndDrawWasRollover) {
      const dialogRef = this.matDialog.open(ShouldHidePlayersNamePdfComponent, {
        width: '650px'
      });

      dialogRef.afterClosed().subscribe({
        next: (shouldShowName: boolean) => {
          this.generatePDF(shouldShowName);
        }
      })
    } else {
      // if the draw winner played their ticket, or the game is set to display the non winner regardless
      this.generatePDF(true);
    }
  }

  generatePDF(showPlayerNameP: boolean) {
    const contentP: Content[] = [];
    if (this.standardGameReport) {

      contentP.push({
        text: `Standard Game Report for Draw Date ${this.datePipe.transform(this.chosenGameInstance?.EndedOn, 'yyyy-MM-dd')}`,
        style: this.pdfUtilityService.PdfStyles.pageTitle,
        margin: [0, 0, 0, 8]
      });

      contentP.push(this.getGameDetails(this.standardGameReport, showPlayerNameP));
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getRefundDetails(this.standardGameReport));
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getPayoutDetails(this.standardGameReport));
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getPotDetails(this.standardGameReport));
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getFeeDetails(this.standardGameReport));
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getCharityDetails(this.standardGameReport));
      contentP.push(this.pdfUtilityService.AddLineBreak());

      const docDef: TDocumentDefinitions = {
        pageMargins: [40, 40, 40, 40],
        footer: (currentPage, pageCount) => {
          return {
            text: 'Page ' + currentPage.toString() + ' of ' + pageCount,
            style: this.pdfUtilityService.PdfStyles.footer
          };
        },
        content: contentP
      };

      const pdf = pdfMake.createPdf(docDef);
      pdf.open();
    }
  }

  public downloadAsCSV(report: IGameInstanceSummaryReport) {
    if (!report) return;

    const formattedDate = this.datePipe.transform(report.DrawTime, 'yyyy-MM-dd')!;
    const filename = `${this.activeGame()?.ShortName}-standard-game-report-${formattedDate}.csv`;

    const lines: string[] = [];

    // Every field goes through csvRow — formatted money carries a thousands separator above $999.99,
    // and an unquoted one splits the value across two columns.
    const row = (...valuesP: unknown[]) => lines.push(this.utilService.csvRow(valuesP));

    // Report title
    lines.push('Standard Game Report');
    row(`Draw Date: ${this.datePipe.transform(report.EndDate, 'MMM d y HH:mm')}`);
    lines.push('');

    // Game Details
    row('License #', report.LicenceNumber);
    row('Game End Date', this.datePipe.transform(report.EndDate, 'MMM d y HH:mm'));
    row('Draw Time', this.datePipe.transform(report.DrawTime, 'MMM d y HH:mm'));
    row('Drawn #', report.WinningNumber);
    row('Drawn Person', report.WinnerName ?? '—');
    row('Voided Tickets', report.VoidedTickets);
    row('Played Tickets', report.PlayedTickets);
    row('Not Played Tickets', report.NotPlayedTickets);
    row('Total Tickets', report.VoidedTickets + report.PlayedTickets + report.NotPlayedTickets);
    lines.push('');

    // Refund Details
    row('Number of Refunds Issued', report.NumberOfRefundedPayments);
    row('Number of Refunded Tickets', report.NumberOfRefundedTickets);
    row('Total Refund Dollar Amount', this.currencyPipe.transform(report.TotalRefundsDollarAmount));
    row('Charity Refund Fees', this.currencyPipe.transform(report.TotalRefundsCharityContribution));
    lines.push('');

    // Dispute Details
    row('Number of Disputes', report.NumberOfDisputedPayments);
    row('Number of Disputed Tickets', report.NumberOfDisputedTickets);
    row('Total Dispute Dollar Amount', this.currencyPipe.transform(report.TotalDisputesDollarAmount));
    row('Stripe Dispute Fees', this.currencyPipe.transform(report.TotalStripeDisputeFees));
    row('Charity Dispute Fees', this.currencyPipe.transform(report.TotalDisputeCharityContribution + report.TotalCharityDisputeFees));
    lines.push('');

    // Payout Details
    row('Winning Number Played', report.WinnerPlayed ? 'Yes' : 'No');
    row('Winner Payout', this.currencyPipe.transform(report.WinnerAmount));
    row('Winner Payout Status', report.WinnerPayoutStatus ? this.winnerPayoutStatusPipe.transform(report.WinnerPayoutStatus as WinnerPayoutStatusTypes) : 'Winner did not play');
    lines.push('');

    // Pot Total Breakdown
    row('Seed Contribution to Pot', this.currencyPipe.transform(report.SeedTotal));
    row('Rollover Contribution', this.currencyPipe.transform(report.PreviousPotTotal));
    row('Gross Sales', this.currencyPipe.transform(report.GrossSales));
    row('Manual Sales', this.currencyPipe.transform(report.ManualSales));
    row('Autoplay Sales', this.currencyPipe.transform(report.AutoplaySales));
    row('Dispute Fees', this.currencyPipe.transform(this.totalDisputeFees));
    row('Pot Total', this.currencyPipe.transform(report.PotTotal));
    lines.push('');

    // Fee Breakdown
    row('Causable Fees', this.currencyPipe.transform(report.TotalCausableFees));
    if (report.GstOnCausableFees && report.GstOnCausableFees > 0) {
      row('GST on Causable Fees', this.currencyPipe.transform(report.GstOnCausableFees));
    }
    if (report.PstOnCausableFees && report.PstOnCausableFees > 0) {
      row(`${this.pstLabel()} on Causable Fees`, this.currencyPipe.transform(report.PstOnCausableFees));
    }
    if (report.HstOnCausableFees && report.HstOnCausableFees > 0 && !report.GstOnCausableFees) {
      row('HST on Causable Fees', this.currencyPipe.transform(report.HstOnCausableFees));
    }
    row('Stripe Fees', this.currencyPipe.transform(report.TotalStripeFees));
    row('Refund Fees', this.currencyPipe.transform(report.TotalRefundsCharityContribution));
    row('Stripe Dispute Fees', this.currencyPipe.transform(report.TotalStripeDisputeFees));
    row('Charity Dispute Fees', this.currencyPipe.transform(report.TotalDisputeCharityContribution + report.TotalCharityDisputeFees));
    row('Charity Host Admin Fees', this.currencyPipe.transform(report.TotalAdministrationCommissionFees));
    row('Total Fees', this.currencyPipe.transform(this.totalFees));
    lines.push('');

    // Charity Revenue Breakdown
    row('Prize Amount', this.currencyPipe.transform(report.TotalPotContribution));
    row('Net Proceeds For Charity', this.currencyPipe.transform(report.TotalCharityContribution));
    lines.push('');

    // Group/Charity breakdown table
    row('Group', 'Gross', 'Refund Fees', 'Dispute Fees', 'Net', 'Number of Players');

    if (report.CharityContributionsBreakdown && report.CharityContributionsBreakdown.length > 0) {
      report.CharityContributionsBreakdown.forEach((charity: any) => {
        const gross = charity.CharityContribution ?? 0;
        const refundFees = charity.RefundAmount ?? 0;
        const disputeFees = charity.DisputeAmount ?? 0;
        const net = gross - refundFees - disputeFees;
        row(
          charity.Name,
          this.currencyPipe.transform(gross),
          this.currencyPipe.transform(refundFees),
          this.currencyPipe.transform(disputeFees),
          this.currencyPipe.transform(net),
          charity.TotalNumberOfPlayers
        );
      });
    }

    const csvContent = lines.join('\n');
    this.utilService.triggerDownload(csvContent, filename);
  }

  private getCharityDetails(standardGameReportP: IGameInstanceSummaryReport): Content[] {
    const charityDetails: Content[] = [];
    let charityBreakdown: ICharityContributionsBreakdown[] | undefined = this.standardGameReport?.CharityContributionsBreakdown;
    const getAnyColSorts = this.determineSortForPDF();
    if (getAnyColSorts && charityBreakdown) {
      charityBreakdown = this.sortColsForPdf(charityBreakdown, getAnyColSorts)
    }

    charityDetails.push(this.pdfUtilityService.generateSectionTitle('Charity Revenue Breakdown'));
    charityDetails.push(this.pdfUtilityService.generateLabelValueRow('Gross Sales: ', `${this.currencyPipe.transform(standardGameReportP.GrossSales)}`));
    charityDetails.push(this.pdfUtilityService.generateLabelValueRow('Prize Amount: ', `(-${this.currencyPipe.transform(standardGameReportP.TotalPotContribution)})`));
    charityDetails.push(this.pdfUtilityService.generateLabelValueRow('Total Fees: ', `(-${this.currencyPipe.transform(this.totalFees)})`));
    charityDetails.push(this.pdfUtilityService.generateLabelValueRow('Net Proceeds for Charity: ', `${this.currencyPipe.transform(standardGameReportP.TotalCharityContribution)}`),);

    const charityBreakdownContent: Content = {
      layout: 'lightHorizontalLines',
      table: {
        headerRows: 1,
        body: [
          [
            {text: 'Group', fontSize: 10},
            {text: 'Total Amount', fontSize: 10},
            {text: 'Number of Players', fontSize: 10}
          ]
        ]
      },
      margin: [0, 16, 0, 16]
    };

    if (charityBreakdown && charityBreakdown?.length > 0) {
      charityBreakdown.forEach((contributionP) => {
        charityBreakdownContent.table.body.push([
          contributionP.Name, `${this.currencyPipe.transform(contributionP.CharityContribution)}`, contributionP.TotalNumberOfPlayers
        ]);
      });
    }

    charityDetails.push(charityBreakdownContent);
    return charityDetails;
  }

  private getRefundDetails(standardGameReportP: IGameInstanceSummaryReport): Content[] {
    const refundDetails: Content[] = [];
    refundDetails.push(this.pdfUtilityService.generateSectionTitle('Refund Details'));

    if (standardGameReportP.NumberOfRefundedPayments > 0) {
      const columnOne = [
        this.pdfUtilityService.generateLabelValueRow('Number of Refunds Issued: ', `${standardGameReportP.NumberOfRefundedPayments}`),
        this.pdfUtilityService.generateLabelValueRow('Number of Refunded Tickets: ', `${standardGameReportP.NumberOfRefundedTickets}`),
        this.pdfUtilityService.generateLabelValueRow('Total Refund Dollar Amount: ', `${this.currencyPipe.transform(standardGameReportP.TotalRefundsDollarAmount)}`),
      ];

      const gameColumns: Content = {
        columns: [columnOne]
      };
      refundDetails.push(gameColumns);
    } else {
      refundDetails.push(this.pdfUtilityService.generateLabelValueRow('Looks like there were no refunds issued during this game instance', ''))
    }

    return refundDetails;
  }

  private getPayoutDetails(standardGameReportP: IGameInstanceSummaryReport): Content[] {
    const payoutDetails: Content[] = [];
    payoutDetails.push(this.pdfUtilityService.generateSectionTitle('Payout Details'));

    let columnOne = [];

    if (this.activeGame()?.Type == GameTypeEnum.GoldRush) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('Winning Number Played: ', `${standardGameReportP.WinnerPlayed}`));
    }

    columnOne.push(this.pdfUtilityService.generateLabelValueRow('Winner Payout: ', `${this.currencyPipe.transform(standardGameReportP.WinnerAmount)}`));

    if (standardGameReportP.WinnerPayoutStatus) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('Winner Payout Status: ', this.winnerPayoutStatusPipe.transform(standardGameReportP.WinnerPayoutStatus)))
    }

    const gameColumns: Content = {
      columns: [columnOne]
    };
    payoutDetails.push(gameColumns);
    return payoutDetails;
  }

  private getGameDetails(standardGameReportP: IGameInstanceSummaryReport, showPlayerNameP: boolean): Content[] {
    const gameDetails: Content[] = [];
    gameDetails.push(this.pdfUtilityService.generateSectionTitle('Game Details'));
    let winnerName = showPlayerNameP ? `${standardGameReportP.WinnerName}` : '-------------';

    const totalTickets = standardGameReportP.VoidedTickets + standardGameReportP.PlayedTickets + standardGameReportP.NotPlayedTickets;

    const columnOne = [
      this.pdfUtilityService.generateLabelValueRow('License #: ', `${standardGameReportP.LicenceNumber}`),
      this.pdfUtilityService.generateLabelValueRow('Game End Date : ', `${this.datePipe.transform(standardGameReportP.EndDate, 'yyyy-MM-dd h:mm a')}`),
      this.pdfUtilityService.generateLabelValueRow('Draw Time : ', `${this.datePipe.transform(standardGameReportP.DrawTime, 'yyyy-MM-dd h:mm a')}`),
      this.pdfUtilityService.generateLabelValueRow('Drawn #: ', `${standardGameReportP.WinningNumber}`),
      this.pdfUtilityService.generateLabelValueRow('Drawn Person: ', winnerName),
      this.pdfUtilityService.generateLabelValueRow('Voided Tickets: ', `${standardGameReportP.VoidedTickets}`),
      this.pdfUtilityService.generateLabelValueRow('Played Tickets: ', `${standardGameReportP.PlayedTickets}`),
      this.pdfUtilityService.generateLabelValueRow('Not Played Tickets: ', `${standardGameReportP.NotPlayedTickets}`),
      this.pdfUtilityService.generateLabelValueRow('Total Tickets: ', `${totalTickets}`)
    ];

    const gameColumns: Content = {
      columns: [columnOne]
    };
    gameDetails.push(gameColumns);
    return gameDetails;
  }

  private getPotDetails(standardGameReportP: IGameInstanceSummaryReport): Content[] {
    const potDetails: Content[] = [];
    potDetails.push(this.pdfUtilityService.generateSectionTitle('Pot Total Breakdown'));

    let columnOne = [];
    columnOne.push(this.pdfUtilityService.generateLabelValueRow('Seed Contribution to Pot: ', `${this.currencyPipe.transform(standardGameReportP.SeedTotal)}`));

    if (this.activeGame()?.Type === GameTypeEnum.GoldRush) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('Rollover Contribution: ', `${this.currencyPipe.transform(standardGameReportP.PreviousPotTotal)}`));
    }

    columnOne.push([
      this.pdfUtilityService.generateLabelValueRow('Refund Total: ', `(-${this.currencyPipe.transform(0)})`),
      this.pdfUtilityService.generateLabelValueRow('Gross Sales: ', `${this.currencyPipe.transform(standardGameReportP.GrossSales)}`),
      this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Manual Sales: ', `${this.currencyPipe.transform(standardGameReportP.ManualSales)}`),
      this.pdfUtilityService.generateLabelValueRowWithLargeLeft('Autoplay Sales: ', `${this.currencyPipe.transform(standardGameReportP.AutoplaySales)}`)
    ]);

    columnOne.push(this.pdfUtilityService.generateLabelValueRow('Pot Total: ', `${this.currencyPipe.transform(standardGameReportP.PotTotal)}`));

    potDetails.push({
      columns: [columnOne]
    });
    return potDetails;
  }

  private getFeeDetails(standardGameReportP: IGameInstanceSummaryReport): Content[] {
    const causableDetails: Content[] = [];
    causableDetails.push(this.pdfUtilityService.generateSectionTitle('Fee Breakdown'));
    const columnOne = [
      this.pdfUtilityService.generateLabelValueRow('Causable Fees: ', `${this.currencyPipe.transform(standardGameReportP.TotalCausableFees)}`),
    ];

    // Add tax breakdown based on what's available
    if (standardGameReportP.GstOnCausableFees && standardGameReportP.GstOnCausableFees > 0) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('GST on Causable Fees: ', `${this.currencyPipe.transform(standardGameReportP.GstOnCausableFees)}`));
    }
    if (standardGameReportP.PstOnCausableFees && standardGameReportP.PstOnCausableFees > 0) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow(`${this.pstLabel()} on Causable Fees: `, `${this.currencyPipe.transform(standardGameReportP.PstOnCausableFees)}`));
    }
    if (standardGameReportP.HstOnCausableFees && standardGameReportP.HstOnCausableFees > 0 && !standardGameReportP.GstOnCausableFees) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('HST on Causable Fees: ', `${this.currencyPipe.transform(standardGameReportP.HstOnCausableFees)}`));
    }

    columnOne.push(
      this.pdfUtilityService.generateLabelValueRow('Stripe Fees: ', `${this.currencyPipe.transform(standardGameReportP.TotalStripeFees)}`),
      this.pdfUtilityService.generateLabelValueRow('Charity Host Admin Fees : ', `${this.currencyPipe.transform(standardGameReportP.TotalAdministrationCommissionFees)}`),
      this.pdfUtilityService.generateLabelValueRow('Total Fees : ', `${this.currencyPipe.transform(this.totalFees)}`),
    );

    causableDetails.push({
      columns: [columnOne]
    });
    return causableDetails;
  }
}
